/**
 * SettingsScreen — Configurações do tenant (leitura, Fase 0).
 *
 * Portada do protótipo preservando identidade visual, estilos inline e copy pt-BR.
 *
 * Ligação com API (F0-BE-2):
 *   GET /settings   → useSettings()   (perm settings:view)
 *
 * Modo degradado: quando o endpoint retorna erro 403 exibe ForbiddenBanner;
 * outros erros exibem QueryError com botão de retry.
 * Enquanto a API não cobrir alguma seção, os dados da query são exibidos
 * e campos ausentes ficam com valor '—' (nunca mock hardcoded).
 *
 * Fase 0: somente leitura. Edições de budget/vendors serão adicionadas em F1+.
 */
import { useMemo, useState } from 'react';
import {
  Settings, Cpu, DollarSign, GitBranch, Shield, Users, ShieldCheck, ChevronRight, Pencil,
  CheckCircle2, X, RefreshCw, Palette, Check, Server, Plus, Trash2, Gauge, FileText,
} from 'lucide-react';
import { C } from '@/theme/palette';
import { useTheme, THEME_LIST, THEMES } from '@/theme';
import { Card, Btn, Badge } from '@/components/ui';
import { MutationError } from '@/components/ui/QueryState';
import { ForbiddenBanner } from '@/components/ui/ForbiddenBanner';
import { LoadingSpinner, QueryError } from '@/components/ui/QueryState';
import { useSettings } from '@/api/settings';
import { useBudget, useSetBudget } from '@/api/dashboard';
import { useStepTokens, useSaveStepTokens } from '@/api/stepTokens';
import {
  useIntegrations, useConnectIntegration, useTestIntegration, useDisconnectIntegration,
  useGitOAuthConnect, OAUTH_GIT_PROVIDERS, INTEGRATION_META,
} from '@/api/integrations';
import {
  useMcpServers, useCreateMcpServer, useDeleteMcpServer, useTestMcpServer,
  useBuiltinTools,
} from '@/api/mcp';
import { useNavigationContext } from '@/navigation';

/** Cards ocultos em Configurações por decisão do dono (2026-10-03). As funções, a tela
 *  "Prompts das Jornadas" (rota `stepPrompts`) e a API continuam: é a EXIBIÇÃO que saiu.
 *  `true` devolve o card. */
const MOSTRAR_TOKENS_POR_STEP = false;
const MOSTRAR_PROMPTS_DAS_JORNADAS = false;

// ─── Primitivos de UI ─────────────────────────────────────────────────────────

/**
 * Toggle visual (read-only — sem onChange) espelhando o protótipo.
 */
function SettingToggle({ on }) {
  return (
    <span
      aria-label={on ? 'Ativo' : 'Inativo'}
      role="img"
      style={{
        width: 36,
        height: 20,
        borderRadius: 10,
        backgroundColor: on ? C.cyber : C.blackElev2,
        border: `1px solid ${on ? C.cyber : C.blackBorder}`,
        position: 'relative',
        display: 'inline-block',
        flexShrink: 0,
      }}
    >
      <span
        aria-hidden="true"
        style={{
          position: 'absolute',
          top: 2,
          left: on ? 18 : 2,
          width: 14,
          height: 14,
          borderRadius: '50%',
          backgroundColor: on ? C.black : C.neutralMuted,
          transition: 'left 150ms',
        }}
      />
    </span>
  );
}

/**
 * SettingRow — linha de configuração com label, valor e toggle ou badge.
 *
 * @param {{ label: string, value?: string, on?: boolean, last?: boolean }} props
 */
function SettingRow({ label, value, on, last }) {
  const hasToggle = on !== undefined;
  const isConn    = !hasToggle && value;
  const connBadgeColor = value === 'pendente' ? C.neutralMuted : C.cyber;
  const connBadgeLabel = value === 'pendente' ? 'PENDENTE' : 'CONECTADO';

  return (
    <div
      className="flex items-center justify-between"
      style={{
        padding: '12px 0',
        borderBottom: last ? 'none' : `1px solid ${C.blackBorder}`,
      }}
    >
      <div style={{ minWidth: 0 }}>
        <div style={{ fontSize: 13, color: C.neutral }}>{label}</div>
        {value && !isConn && (
          <div style={{ fontSize: 12, color: C.neutralDim, marginTop: 2 }}>{value}</div>
        )}
        {value && isConn && hasToggle && (
          <div style={{ fontSize: 12, color: C.neutralDim, marginTop: 2 }}>{value}</div>
        )}
      </div>
      {hasToggle ? (
        <SettingToggle on={on} />
      ) : isConn ? (
        <Badge color={connBadgeColor} light>{connBadgeLabel}</Badge>
      ) : null}
    </div>
  );
}

// ─── Helpers de renderização de seções ───────────────────────────────────────

/**
 * Formata o valor de budget restante em % a partir dos dados da API.
 * Fallback: '—' se os dados não chegarem.
 */
// ─── Card de Integrações (conectar/testar/desconectar) ─────────────────────────

function IntegrationRow({ intg, meta }) {
  const connect    = useConnectIntegration();
  const test       = useTestIntegration();
  const disconnect = useDisconnectIntegration();
  const gitOAuth   = useGitOAuthConnect();       // "conectar com sua conta" (GitHub/Bitbucket)
  const [editing, setEditing] = useState(false);
  const [form,    setForm]    = useState({});
  const hasOAuth = OAUTH_GIT_PROVIDERS.includes(intg.provider);   // github | bitbucket
  const manualLabel = intg.provider === 'github' ? 'Colar token'
    : intg.provider === 'bitbucket' ? 'App password'
    : (intg.connected ? 'Reconfigurar' : 'Conectar');

  const fields = Object.entries(meta.fields ?? {});
  // Conexão global do servidor (via .env) — read-only na UI: não dá pra
  // desconectar/reconfigurar uma chave de ambiente pela tela.
  const isEnv = intg.source === 'env';

  const open = () => {
    setForm(Object.fromEntries(fields.map(([k]) => [k, ''])));
    connect.reset();
    setEditing(true);
  };
  const submit = () => {
    connect.mutate(
      { provider: intg.provider, config: form },
      { onSuccess: () => setEditing(false) },
    );
  };

  return (
    <div style={{ padding: '10px 0', borderTop: `1px solid ${C.blackBorder}` }}>
      <div className="flex items-center justify-between" style={{ gap: 8 }}>
        <div className="flex items-center gap-2" style={{ minWidth: 0 }}>
          {intg.connected
            ? <CheckCircle2 size={14} style={{ color: C.cyber, flexShrink: 0 }} aria-hidden="true" />
            : <span style={{ width: 8, height: 8, borderRadius: 999, backgroundColor: C.neutralMuted, flexShrink: 0 }} />}
          <div style={{ minWidth: 0 }}>
            <div style={{ fontSize: 13.5, color: C.neutral, fontWeight: 500 }}>{meta.label}</div>
            <div style={{ fontSize: 11.5, color: intg.connected ? C.cyber : C.neutralMuted, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {intg.connected ? (intg.account_label || 'conectado') : 'não conectado'}
            </div>
          </div>
        </div>
        {!editing && (
          <div className="flex items-center gap-1" style={{ flexShrink: 0 }}>
            {isEnv ? (
              <span style={{
                fontSize: 10.5, fontWeight: 600, letterSpacing: 0.3,
                color: C.neutralMuted, border: `1px solid ${C.blackBorder}`,
                borderRadius: 999, padding: '2px 8px', whiteSpace: 'nowrap',
              }}>
                VIA AMBIENTE
              </span>
            ) : (
              <>
                {intg.connected && (
                  <>
                    <Btn variant="ghost" size="sm" icon={RefreshCw}
                      onClick={() => test.mutate(intg.provider)}
                      aria-busy={test.isPending}>
                      {test.isPending ? '...' : 'Testar'}
                    </Btn>
                    <Btn variant="ghost" size="sm" icon={X}
                      onClick={() => disconnect.mutate(intg.provider)}>
                      Desconectar
                    </Btn>
                  </>
                )}
                {hasOAuth && (
                  <Btn variant="primary" size="sm" onClick={() => gitOAuth.mutate(intg.provider)}
                    disabled={gitOAuth.isPending} aria-busy={gitOAuth.isPending}>
                    {gitOAuth.isPending ? 'Conectando…' : (intg.connected ? 'Reconectar' : `Conectar com ${meta.label}`)}
                  </Btn>
                )}
                <Btn variant="outline" size="sm" onClick={open}>
                  {manualLabel}
                </Btn>
              </>
            )}
          </div>
        )}
      </div>

      {test.isError && !editing && (
        <div style={{ marginTop: 6 }}><MutationError error={test.error} onDismiss={() => test.reset()} /></div>
      )}

      {gitOAuth.isError && !editing && (
        <div style={{ marginTop: 6 }}><MutationError error={gitOAuth.error} onDismiss={() => gitOAuth.reset()} /></div>
      )}

      {editing && (
        <div style={{ marginTop: 10, padding: 12, borderRadius: 8, backgroundColor: C.black, border: `1px solid ${C.blackBorder}` }}>
          <MutationError error={connect.error} onDismiss={() => connect.reset()} />
          {fields.map(([key, f]) => (
            <div key={key} style={{ marginBottom: 8 }}>
              <label htmlFor={`intg-${intg.provider}-${key}`} style={{ display: 'block', fontSize: 11.5, color: C.neutralDim, marginBottom: 4 }}>
                {f.label}
              </label>
              <input
                id={`intg-${intg.provider}-${key}`}
                type={f.secret ? 'password' : 'text'}
                value={form[key] ?? ''}
                onChange={(e) => setForm((s) => ({ ...s, [key]: e.target.value }))}
                placeholder={f.placeholder || ''}
                autoComplete={f.secret ? 'new-password' : 'off'}
                style={{
                  width: '100%', padding: '8px 10px', backgroundColor: C.blackElev2,
                  border: `1px solid ${C.blackBorder}`, borderRadius: 6,
                  color: C.neutral, fontSize: 13, boxSizing: 'border-box',
                }}
              />
            </div>
          ))}
          <div className="flex items-center gap-2" style={{ marginTop: 4 }}>
            <Btn variant="primary" size="sm" onClick={submit} disabled={connect.isPending}>
              {connect.isPending ? 'Testando e salvando...' : 'Conectar'}
            </Btn>
            <Btn variant="ghost" size="sm" onClick={() => setEditing(false)}>Cancelar</Btn>
          </div>
        </div>
      )}
    </div>
  );
}

function IntegrationsCard({ category = 'scm', title, description, icon: Icon = GitBranch }) {
  const { data, isLoading } = useIntegrations();
  const items = (Array.isArray(data) ? data : []).filter(
    (i) => (i.category ?? 'scm') === category,
  );

  return (
    <Card>
      <div className="flex items-center gap-2 mb-1">
        <Icon size={16} style={{ color: C.cyber }} aria-hidden="true" />
        <h3 style={{ fontSize: 15, fontWeight: 600, color: C.neutral }}>{title}</h3>
      </div>
      <p style={{ fontSize: 12, color: C.neutralMuted, margin: '0 0 4px' }}>{description}</p>
      {isLoading ? (
        <LoadingSpinner label="Carregando..." size={18} />
      ) : (
        items.map((intg) => (
          <IntegrationRow
            key={intg.provider}
            intg={intg}
            meta={INTEGRATION_META[intg.provider] ?? { label: intg.provider, fields: {} }}
          />
        ))
      )}
    </Card>
  );
}

// ─── Card de Budget (editável) ─────────────────────────────────────────────────

/**
 * BudgetCard — orçamento mensal editável (GET/PUT /dashboard/budget).
 * Os demais itens (alerta, bloqueio) vêm de GET /settings (somente leitura).
 */
function BudgetCard({ settingsBudget }) {
  const budgetQuery = useBudget();
  const setBudget   = useSetBudget();
  const [editing, setEditing] = useState(false);
  const [input,   setInput]   = useState('');

  const amount   = budgetQuery.data?.monthlyAmount;
  const currency = budgetQuery.data?.currency ?? 'USD';

  const open = () => {
    setInput(amount != null ? String(amount) : '');
    setBudget.reset();
    setEditing(true);
  };
  const save = () => {
    const val = parseFloat(String(input).replace(/\./g, '').replace(',', '.'));
    if (!Number.isFinite(val) || val < 0) return;
    setBudget.mutate({ monthlyAmount: val, currency }, { onSuccess: () => setEditing(false) });
  };

  return (
    <Card>
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <DollarSign size={16} style={{ color: C.cyber }} aria-hidden="true" />
          <h3 style={{ fontSize: 15, fontWeight: 600, color: C.neutral }}>Budget &amp; limites</h3>
        </div>
        {!editing && (
          <Btn variant="ghost" size="sm" icon={Pencil} onClick={open}>Editar</Btn>
        )}
      </div>

      {editing ? (
        <div style={{ padding: '8px 0 12px' }}>
          <label htmlFor="settings-budget" style={{ display: 'block', fontSize: 12, color: C.neutralDim, marginBottom: 6 }}>
            Budget mensal ({currency})
          </label>
          <div className="flex items-center gap-2 flex-wrap">
            <input
              id="settings-budget"
              type="text"
              inputMode="decimal"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') save(); }}
              placeholder="5000"
              autoFocus
              style={{
                width: 160, padding: '8px 10px', backgroundColor: C.black,
                border: `1px solid ${C.blackBorder}`, borderRadius: 6,
                color: C.neutral, fontSize: 13, outline: 'none',
              }}
            />
            <Btn variant="primary" size="sm" onClick={save} disabled={setBudget.isPending}>
              {setBudget.isPending ? 'Salvando...' : 'Salvar'}
            </Btn>
            <Btn variant="ghost" size="sm" onClick={() => setEditing(false)}>Cancelar</Btn>
          </div>
          {setBudget.isError && (
            <div style={{ fontSize: 12, color: C.hot, marginTop: 6 }}>
              {setBudget.error?.status === 403
                ? 'Sem permissão para editar o orçamento (requer Admin ou Manager).'
                : `Falha ao salvar: ${String(setBudget.error?.message || setBudget.error)}`}
            </div>
          )}
        </div>
      ) : (
        <SettingRow
          label="Budget mensal"
          value={amount != null
            ? `US$ ${Number(amount).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`
            : '—'}
        />
      )}

      <SettingRow
        label="Alerta de consumo"
        value={settingsBudget.alert_threshold_pct
          ? `${settingsBudget.alert_threshold_pct}% do budget`
          : '80% do budget'}
        on={!!settingsBudget.alert_threshold_pct || true}
      />
      <SettingRow
        label="Bloqueio ao exceder"
        value="Pausar execuções acima de 100%"
        on={settingsBudget.block_on_exceed ?? true}
        last
      />
    </Card>
  );
}

// ─── Card de Tokens por Step (max_output_tokens editável por step) ────────────

/**
 * StepTokensCard — ajusta o teto de `max_output_tokens` por step do produto
 * (GET/PUT /settings/step-tokens). Agrupa os steps por fase (accordion).
 *
 * Permissão de escrita: a MESMA usada pelo card de Budget (Admin/Manager) —
 * não há endpoint de verificação prévia, então a UI reage ao 403 do PUT do
 * mesmo jeito que o BudgetCard: mostra a mensagem e passa a tratar os campos
 * como somente leitura (desabilitados) para o restante da sessão da tela.
 */
function StepTokensCard() {
  const { data, isLoading, error, refetch } = useStepTokens();
  const save = useSaveStepTokens();
  const [edits, setEdits] = useState({});
  const [openPhases, setOpenPhases] = useState({});
  const [forbidden, setForbidden] = useState(false);
  const [savedMsg, setSavedMsg] = useState(false);

  const steps = Array.isArray(data?.steps) ? data.steps : [];
  const globalDefault = data?.global_default;

  // Agrupa por fase preservando a ordem de chegada do backend.
  const groups = useMemo(() => {
    const map = new Map();
    steps.forEach((st) => {
      const phase = st.phase || 'Geral';
      if (!map.has(phase)) map.set(phase, []);
      map.get(phase).push(st);
    });
    return Array.from(map.entries());
  }, [steps]);

  const isOpen = (phase) => openPhases[phase] ?? true;
  const togglePhase = (phase) => setOpenPhases((s) => ({ ...s, [phase]: !isOpen(phase) }));

  const valueOf = (st) => (Object.prototype.hasOwnProperty.call(edits, st.key)
    ? edits[st.key]
    : (st.override != null ? String(st.override) : ''));

  const onChange = (key, v) => {
    setSavedMsg(false);
    setEdits((s) => ({ ...s, [key]: v }));
  };

  // valor final a enviar para um step (null = restaura o default).
  const resolvedValue = (key) => {
    const raw = edits[key];
    const n = parseInt(raw, 10);
    return (!raw || !Number.isFinite(n) || n <= 0) ? null : n;
  };

  const dirtyKeys = Object.keys(edits).filter((key) => {
    const st = steps.find((s) => s.key === key);
    if (!st) return false;
    return resolvedValue(key) !== (st.override ?? null);
  });

  const handleError = (err) => { if (err?.status === 403) setForbidden(true); };

  const restore = (key) => {
    setSavedMsg(false);
    save.mutate([{ key, max_output_tokens: null }], {
      onSuccess: () => {
        setEdits((s) => { const c = { ...s }; delete c[key]; return c; });
        setSavedMsg(true);
      },
      onError: handleError,
    });
  };

  const onSave = () => {
    setSavedMsg(false);
    const payload = dirtyKeys.map((key) => ({ key, max_output_tokens: resolvedValue(key) }));
    if (!payload.length) return;
    save.mutate(payload, {
      onSuccess: () => { setEdits({}); setSavedMsg(true); },
      onError: handleError,
    });
  };

  const header = (
    <div className="flex items-center gap-2 mb-1">
      <Gauge size={16} style={{ color: C.cyber }} aria-hidden="true" />
      <h3 style={{ fontSize: 15, fontWeight: 600, color: C.neutral }}>Tokens por Step</h3>
    </div>
  );

  if (isLoading) {
    return (
      <Card style={{ gridColumn: '1 / -1' }}>
        {header}
        <LoadingSpinner label="Carregando..." size={18} />
      </Card>
    );
  }

  if (error?.isForbidden) {
    return (
      <Card style={{ gridColumn: '1 / -1' }}>
        {header}
        <div style={{ fontSize: 12.5, color: C.neutralDim }}>
          Sem permissão para visualizar os tetos de tokens por step.
        </div>
      </Card>
    );
  }

  if (error) {
    return (
      <Card style={{ gridColumn: '1 / -1' }}>
        {header}
        <QueryError error={error} onRetry={refetch} />
      </Card>
    );
  }

  return (
    <Card style={{ gridColumn: '1 / -1' }}>
      <div className="flex items-center justify-between mb-1">
        {header}
        <Btn variant="primary" size="sm" onClick={onSave}
          disabled={forbidden || save.isPending || dirtyKeys.length === 0}>
          {save.isPending ? 'Salvando...' : 'Salvar'}
        </Btn>
      </div>
      <p style={{ fontSize: 12, color: C.neutralMuted, margin: '0 0 8px' }}>
        Ajuste o teto de <code style={{ fontSize: 11.5 }}>max_output_tokens</code> de cada step do
        produto. Default global: {globalDefault != null ? globalDefault.toLocaleString('pt-BR') : '—'}.
        O teto real por chamada é limitado pelo modelo; geração estruturada costura continuações e
        passa disso.
      </p>

      {forbidden && (
        <div role="alert" style={{ fontSize: 12, color: C.hot, margin: '4px 0 10px' }}>
          Sem permissão para editar o orçamento (requer Admin ou Manager).
        </div>
      )}
      {savedMsg && !forbidden && (
        <div role="status" style={{ fontSize: 12, color: C.cyber, margin: '4px 0 10px' }}>
          Alterações salvas.
        </div>
      )}
      {save.isError && !forbidden && (
        <div style={{ marginBottom: 10 }}>
          <MutationError error={save.error} onDismiss={() => save.reset()} />
        </div>
      )}

      {groups.length === 0 && (
        <div style={{ fontSize: 12.5, color: C.neutralMuted }}>Nenhum step configurável ainda.</div>
      )}

      {groups.map(([phase, list]) => (
        <div key={phase} style={{ marginTop: 10, borderTop: `1px solid ${C.blackBorder}`, paddingTop: 10 }}>
          <button
            type="button"
            onClick={() => togglePhase(phase)}
            aria-expanded={isOpen(phase)}
            className="flex items-center justify-between"
            style={{ width: '100%', background: 'transparent', border: 'none', cursor: 'pointer', padding: 0 }}
          >
            <span style={{ fontSize: 12, fontWeight: 700, color: C.neutral, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
              {phase}
            </span>
            <ChevronRight
              size={14}
              aria-hidden="true"
              style={{
                color: C.neutralMuted,
                transform: isOpen(phase) ? 'rotate(90deg)' : 'none',
                transition: 'transform 150ms',
              }}
            />
          </button>

          {isOpen(phase) && (
            <div style={{ marginTop: 8, display: 'flex', flexDirection: 'column', gap: 10 }}>
              {list.map((st) => {
                const changed = dirtyKeys.includes(st.key);
                return (
                  <div key={st.key} className="flex items-center justify-between gap-2" style={{ flexWrap: 'wrap' }}>
                    <div style={{ minWidth: 0, flex: '1 1 220px' }}>
                      <div style={{ fontSize: 13, color: C.neutral }}>{st.label}</div>
                      <div style={{ fontSize: 11, color: C.neutralMuted, marginTop: 2 }}>
                        default {(st.code_default ?? 0).toLocaleString('pt-BR')}
                        {' · '}efetivo {(st.effective ?? 0).toLocaleString('pt-BR')}
                        {st.override != null ? ' · override ativo' : ''}
                      </div>
                    </div>
                    <div className="flex items-center gap-1" style={{ flexShrink: 0 }}>
                      <label htmlFor={`step-tok-${st.key}`} className="sr-only" style={{ position: 'absolute', width: 1, height: 1, overflow: 'hidden' }}>
                        Teto de tokens para {st.label}
                      </label>
                      <input
                        id={`step-tok-${st.key}`}
                        type="number"
                        min={1}
                        step={1}
                        inputMode="numeric"
                        value={valueOf(st)}
                        onChange={(e) => onChange(st.key, e.target.value)}
                        disabled={forbidden}
                        placeholder={String(st.effective ?? st.code_default ?? '')}
                        style={{
                          width: 120, padding: '6px 8px', backgroundColor: C.black,
                          border: `1px solid ${changed ? C.cyber : C.blackBorder}`, borderRadius: 6,
                          color: C.neutral, fontSize: 12.5, outline: 'none',
                        }}
                      />
                      {st.override != null && (
                        <Btn variant="ghost" size="sm" onClick={() => restore(st.key)} disabled={forbidden || save.isPending}>
                          Restaurar padrão
                        </Btn>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      ))}
    </Card>
  );
}

// ─── Seletor de tema (aparência) ──────────────────────────────────────────────

/**
 * ThemeSelectorCard — 5 temas selecionáveis com pré-visualização de cores.
 * A troca é imediata (remount) e persistida em localStorage.
 */
function ThemeSelectorCard({ theme, setTheme }) {
  return (
    <Card style={{ gridColumn: '1 / -1' }}>
      <div className="flex items-center gap-2 mb-1">
        <Palette size={16} style={{ color: C.cyber }} aria-hidden="true" />
        <h3 style={{ fontSize: 15, fontWeight: 600, color: C.neutral }}>Aparência</h3>
      </div>
      <p style={{ fontSize: 12, color: C.neutralDim, marginBottom: 14 }}>
        Escolha o tema da interface. A preferência fica salva neste navegador.
      </p>

      <div
        role="radiogroup"
        aria-label="Tema da interface"
        className="grid gap-3"
        style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))' }}
      >
        {THEME_LIST.map((t) => {
          const pal      = THEMES[t.id];
          const selected = theme === t.id;
          return (
            <button
              key={t.id}
              type="button"
              role="radio"
              aria-checked={selected}
              onClick={() => setTheme(t.id)}
              style={{
                textAlign: 'left',
                padding: 12,
                borderRadius: 10,
                cursor: 'pointer',
                backgroundColor: C.blackElev,
                border: `1px solid ${selected ? C.cyber : C.blackBorder}`,
                boxShadow: selected ? `0 0 0 1px ${C.cyber}` : 'none',
                transition: 'all 150ms',
              }}
            >
              {/* Pré-visualização (swatches do tema) */}
              <div
                aria-hidden="true"
                style={{
                  display: 'flex',
                  height: 36,
                  borderRadius: 6,
                  overflow: 'hidden',
                  border: `1px solid ${C.blackBorder}`,
                  marginBottom: 10,
                }}
              >
                <div style={{ flex: 2, backgroundColor: pal.black }} />
                <div style={{ flex: 1, backgroundColor: pal.cyber }} />
                <div style={{ flex: 1, backgroundColor: pal.quantum }} />
                <div style={{ flex: 1, backgroundColor: pal.neutral }} />
              </div>

              <div className="flex items-center justify-between">
                <span style={{ fontSize: 13, fontWeight: 600, color: C.neutral }}>{t.label}</span>
                {selected && <Check size={15} style={{ color: C.cyber }} aria-hidden="true" />}
              </div>
              <div style={{ fontSize: 11, color: C.neutralMuted, marginTop: 2 }}>{t.hint}</div>
            </button>
          );
        })}
      </div>
    </Card>
  );
}

// ─── Card de Servidores MCP (Model Context Protocol) ───────────────────────────

/** Mensagem amigável (pt-BR) a partir de um erro de mutação MCP. */
function mcpFriendlyError(error, fallback) {
  if (!error) return null;
  if (error.status === 403 || error.isForbidden) {
    return 'Sem permissão para gerenciar servidores MCP (requer Admin ou Manager).';
  }
  return fallback;
}

const MCP_FIELD_STYLE = {
  width: '100%', padding: '8px 10px', backgroundColor: C.blackElev2,
  border: `1px solid ${C.blackBorder}`, borderRadius: 6,
  color: C.neutral, fontSize: 13, boxSizing: 'border-box',
};

/** Linha de um servidor MCP com ações Testar / Remover. */
function McpServerRow({ server }) {
  const test   = useTestMcpServer();
  const remove = useDeleteMcpServer();

  const toolsCount = test.isSuccess ? (test.data?.tools_count ?? 0) : null;
  const testErr    = mcpFriendlyError(test.error, 'Falha ao testar o servidor MCP. Verifique a URL e a autenticação.');
  const removeErr  = mcpFriendlyError(remove.error, 'Não foi possível remover o servidor MCP. Tente novamente.');

  return (
    <div style={{ padding: '10px 0', borderTop: `1px solid ${C.blackBorder}` }}>
      <div className="flex items-center justify-between" style={{ gap: 8 }}>
        <div className="flex items-center gap-2" style={{ minWidth: 0 }}>
          <Server
            size={14}
            style={{ color: server.is_active ? C.cyber : C.neutralMuted, flexShrink: 0 }}
            aria-hidden="true"
          />
          <div style={{ minWidth: 0 }}>
            <div className="flex items-center gap-2" style={{ flexWrap: 'wrap' }}>
              <span style={{ fontSize: 13.5, color: C.neutral, fontWeight: 500 }}>{server.name}</span>
              {server.has_auth && <Badge color={C.quantum} light>com auth</Badge>}
              {server.is_active === false && <Badge color={C.neutralMuted} light>inativo</Badge>}
            </div>
            <div style={{ fontSize: 11.5, color: C.neutralMuted, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {server.base_url}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-1" style={{ flexShrink: 0 }}>
          <Btn variant="ghost" size="sm" icon={RefreshCw}
            onClick={() => test.mutate({ id: server.id })}
            aria-busy={test.isPending}>
            {test.isPending ? '...' : 'Testar'}
          </Btn>
          <Btn variant="ghost" size="sm" icon={Trash2}
            onClick={() => remove.mutate({ id: server.id })}
            aria-label={`Remover servidor ${server.name}`}
            disabled={remove.isPending}>
            {remove.isPending ? '...' : 'Remover'}
          </Btn>
        </div>
      </div>

      {test.isSuccess && (
        <div role="status" style={{ fontSize: 12, color: C.cyber, marginTop: 6 }}>
          Conexão OK — {toolsCount} {toolsCount === 1 ? 'tool disponível' : 'tools disponíveis'}.
        </div>
      )}
      {testErr && (
        <div role="alert" style={{ fontSize: 12, color: C.hot, marginTop: 6 }}>{testErr}</div>
      )}
      {removeErr && (
        <div role="alert" style={{ fontSize: 12, color: C.hot, marginTop: 6 }}>{removeErr}</div>
      )}
    </div>
  );
}

/**
 * McpServersCard — lista/cadastra/remove/testa servidores MCP do tenant.
 * Servem como executores de tools (skills marcadas como tool com executor "mcp").
 */
function McpServersCard() {
  const { data, isLoading, error } = useMcpServers();
  const builtins = useBuiltinTools();
  const create = useCreateMcpServer();
  const [adding, setAdding] = useState(false);
  const [form,   setForm]   = useState({ name: '', base_url: '', auth_header: '' });

  const servers = Array.isArray(data) ? data : [];

  const open = () => {
    setForm({ name: '', base_url: '', auth_header: '' });
    create.reset();
    setAdding(true);
  };
  const submit = (e) => {
    e.preventDefault();
    create.mutate(
      {
        name:        form.name,
        base_url:    form.base_url,
        // auth_header é opcional — só envia quando preenchido.
        ...(form.auth_header.trim() ? { auth_header: form.auth_header } : {}),
      },
      { onSuccess: () => setAdding(false) },
    );
  };

  const listErr   = mcpFriendlyError(error, 'Não foi possível carregar os servidores MCP.');
  const createErr = mcpFriendlyError(create.error, 'Não foi possível adicionar o servidor MCP. Verifique os dados e tente novamente.');

  return (
    <Card style={{ gridColumn: '1 / -1' }}>
      <div className="flex items-center justify-between mb-1">
        <div className="flex items-center gap-2">
          <Server size={16} style={{ color: C.cyber }} aria-hidden="true" />
          <h3 style={{ fontSize: 15, fontWeight: 600, color: C.neutral }}>Servidores MCP</h3>
        </div>
        {!adding && (
          <Btn variant="outline" size="sm" icon={Plus} onClick={open}>Adicionar</Btn>
        )}
      </div>
      <p style={{ fontSize: 12, color: C.neutralMuted, margin: '0 0 4px' }}>
        Conecte servidores MCP (Model Context Protocol) para expor tools às skills executáveis.
        As credenciais são cifradas em repouso.
      </p>

      {adding && (
        <form
          onSubmit={submit}
          aria-label="Adicionar servidor MCP"
          style={{ marginTop: 10, marginBottom: 6, padding: 12, borderRadius: 8, backgroundColor: C.black, border: `1px solid ${C.blackBorder}` }}
        >
          {createErr && (
            <div role="alert" style={{ fontSize: 12, color: C.hot, marginBottom: 8 }}>{createErr}</div>
          )}
          <div style={{ marginBottom: 8 }}>
            <label htmlFor="mcp-name" style={{ display: 'block', fontSize: 11.5, color: C.neutralDim, marginBottom: 4 }}>Nome</label>
            <input
              id="mcp-name"
              type="text"
              required
              value={form.name}
              onChange={(e) => setForm((s) => ({ ...s, name: e.target.value }))}
              placeholder="meu-servidor-mcp"
              style={MCP_FIELD_STYLE}
            />
          </div>
          <div style={{ marginBottom: 8 }}>
            <label htmlFor="mcp-base-url" style={{ display: 'block', fontSize: 11.5, color: C.neutralDim, marginBottom: 4 }}>URL base</label>
            <input
              id="mcp-base-url"
              type="url"
              required
              value={form.base_url}
              onChange={(e) => setForm((s) => ({ ...s, base_url: e.target.value }))}
              placeholder="https://mcp.exemplo.com"
              style={MCP_FIELD_STYLE}
            />
          </div>
          <div style={{ marginBottom: 10 }}>
            <label htmlFor="mcp-auth-header" style={{ display: 'block', fontSize: 11.5, color: C.neutralDim, marginBottom: 4 }}>
              Cabeçalho de autenticação (opcional)
            </label>
            <input
              id="mcp-auth-header"
              type="password"
              value={form.auth_header}
              onChange={(e) => setForm((s) => ({ ...s, auth_header: e.target.value }))}
              placeholder="Authorization: Bearer ..."
              autoComplete="new-password"
              style={MCP_FIELD_STYLE}
            />
          </div>
          <div className="flex items-center gap-2">
            <Btn type="submit" variant="primary" size="sm" disabled={create.isPending || !form.name.trim() || !form.base_url.trim()}>
              {create.isPending ? 'Adicionando...' : 'Adicionar servidor'}
            </Btn>
            <Btn variant="ghost" size="sm" onClick={() => setAdding(false)} disabled={create.isPending}>
              Cancelar
            </Btn>
          </div>
        </form>
      )}

      {listErr && (
        <div role="alert" style={{ fontSize: 12, color: C.hot, marginTop: 8 }}>{listErr}</div>
      )}

      {isLoading ? (
        <LoadingSpinner label="Carregando servidores..." size={18} />
      ) : servers.length === 0 && !listErr ? (
        <div style={{ fontSize: 12.5, color: C.neutralDim, padding: '10px 0', borderTop: `1px solid ${C.blackBorder}` }}>
          Nenhum servidor MCP cadastrado.
        </div>
      ) : (
        servers.map((server) => <McpServerRow key={server.id} server={server} />)
      )}

      {/* ── Ferramentas nativas (built-in) — não são MCP, vêm compiladas ────── */}
      <div style={{ marginTop: 18 }}>
        <div className="flex items-center gap-2" style={{ marginBottom: 2 }}>
          <h4 style={{ fontSize: 13.5, fontWeight: 600, color: C.neutral }}>
            Ferramentas nativas (built-in)
          </h4>
        </div>
        <p style={{ fontSize: 12, color: C.neutralMuted, margin: '0 0 4px' }}>
          Tools da plataforma disponíveis para as skills sem cadastrar servidor MCP.
        </p>
        {(builtins.data?.items ?? []).map((t) => (
          <div
            key={t.name}
            style={{ padding: '10px 0', borderTop: `1px solid ${C.blackBorder}` }}
            className="flex items-center justify-between"
          >
            <div className="flex items-center gap-2" style={{ minWidth: 0 }}>
              <CheckCircle2 size={14} style={{ color: C.cyber, flexShrink: 0 }} aria-hidden="true" />
              <div style={{ minWidth: 0 }}>
                <div style={{ fontSize: 13.5, color: C.neutral, fontWeight: 500 }}>
                  {t.provider} · <code style={{ fontSize: 12.5, color: C.cyber }}>{t.name}</code>
                </div>
                <div style={{ fontSize: 11.5, color: C.neutralMuted, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {t.label}
                </div>
              </div>
            </div>
            <span style={{
              fontSize: 10.5, fontWeight: 600, letterSpacing: 0.3,
              color: t.active ? C.cyber : C.neutralMuted,
              border: `1px solid ${C.blackBorder}`, borderRadius: 999,
              padding: '2px 8px', whiteSpace: 'nowrap',
            }}>
              {t.active ? 'ATIVO' : 'INATIVO'}
            </span>
          </div>
        ))}
      </div>
    </Card>
  );
}

// ─── Componente principal ─────────────────────────────────────────────────────

export function SettingsScreen() {
  const { navigate }  = useNavigationContext();
  const { theme, setTheme } = useTheme();
  const { data, isLoading, error, refetch } = useSettings();

  // ── Estados de loading / erro ─────────────────────────────────────────────
  if (isLoading) {
    return (
      <div style={{ padding: 28 }}>
        <LoadingSpinner label="Carregando configurações..." />
      </div>
    );
  }

  if (error?.isForbidden) {
    return (
      <div style={{ padding: 28 }}>
        <ForbiddenBanner detail={error.detail} />
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ padding: 28 }}>
        <QueryError error={error} onRetry={refetch} />
      </div>
    );
  }

  // ── Extrair dados (com fallback '—' para campos ausentes) ─────────────────
  const tenant       = data?.tenant       ?? {};
  const budget       = data?.budget       ?? {};
  const security     = data?.security     ?? {};
  const members      = data?.members      ?? [];

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <div style={{ padding: 28, maxWidth: 1100 }}>
      <h1
        style={{
          fontSize: 26,
          fontWeight: 700,
          color: C.neutral,
          letterSpacing: '-0.02em',
          margin: 0,
        }}
      >
        Configurações
      </h1>
      <p
        style={{
          fontSize: 13,
          color: C.neutralDim,
          marginTop: 4,
          marginBottom: 24,
        }}
      >
        Parâmetros do tenant, vendors de IA, budget, integrações, segurança e controle de acessos.
      </p>

      <div
        className="grid gap-4"
        style={{ gridTemplateColumns: '1fr 1fr', alignItems: 'start' }}
      >
        {/* Aparência / Tema */}
        <ThemeSelectorCard theme={theme} setTheme={setTheme} />

        {/* Tenant */}
        <Card>
          <div className="flex items-center gap-2 mb-2">
            <Settings size={16} style={{ color: C.cyber }} aria-hidden="true" />
            <h3 style={{ fontSize: 15, fontWeight: 600, color: C.neutral }}>Tenant</h3>
          </div>
          <SettingRow label="Organização"              value={tenant.name   ?? '—'} />
          <SettingRow label="Plano"                    value={tenant.plan   ?? '—'} />
          <SettingRow label="Região de processamento"  value={tenant.region ?? '—'} last />
        </Card>

        {/* Vendors de IA (cofre por tenant: OpenAI/Anthropic/Gemini/Azure) */}
        <IntegrationsCard
          category="ai"
          title="Vendors de IA"
          icon={Cpu}
          description="Conecte OpenAI, Anthropic, Google Gemini e Azure OpenAI. As chaves são cifradas em repouso."
        />

        {/* Budget & limites (editável) */}
        <BudgetCard settingsBudget={budget} />

        {/* Tokens por Step (max_output_tokens editável por step) */}
        {MOSTRAR_TOKENS_POR_STEP && <StepTokensCard />}

        {/* Card de acesso à tela "Prompts das Jornadas" — ocupa toda a largura */}
        {MOSTRAR_PROMPTS_DAS_JORNADAS && (
        <Card style={{ gridColumn: '1 / -1', borderColor: `${C.cyber}33` }}>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <FileText size={16} style={{ color: C.cyber }} aria-hidden="true" />
              <h3 style={{ fontSize: 15, fontWeight: 600, color: C.neutral }}>
                Prompts das Jornadas
              </h3>
            </div>
            <Btn
              variant="outline"
              size="sm"
              icon={ChevronRight}
              onClick={() => navigate('stepPrompts')}
              aria-label="Ir para Prompts das Jornadas"
            >
              Editar prompts das jornadas
            </Btn>
          </div>
          <p style={{ fontSize: 13, color: C.neutralDim, lineHeight: 1.5, marginTop: 8, marginBottom: 0 }}>
            Edite o prompt de sistema base de cada etapa da jornada (Design, Tecnologia, Análise,
            Backlog, Squad, Conferência, Wizard). Vazio volta ao padrão do sistema.
          </p>
        </Card>
        )}

        {/* Integrações (cofre por tenant: GitHub/Bitbucket/Jira) */}
        <IntegrationsCard
          category="scm"
          title="Integrações"
          icon={GitBranch}
          description="Conecte GitHub, Bitbucket e Jira. As credenciais são cifradas em repouso."
        />

        {/* Servidores MCP (executores de tools para skills executáveis) */}
        <McpServersCard />

        {/* Segurança */}
        <Card>
          <div className="flex items-center gap-2 mb-2">
            <Shield size={16} style={{ color: C.cyber }} aria-hidden="true" />
            <h3 style={{ fontSize: 15, fontWeight: 600, color: C.neutral }}>Segurança</h3>
          </div>
          <SettingRow
            label="SSO corporativo (OAuth 2.0 / OIDC)"
            value="ativo"
            on={security.sso_enabled ?? true}
          />
          <SettingRow
            label="MFA obrigatório para Admins"
            on={security.mfa_required_admin ?? true}
          />
          <SettingRow
            label="Sandbox efêmero (Firecracker)"
            value={security.sandbox_ttl_min
              ? `TTL ${security.sandbox_ttl_min}min · filesystem descartado`
              : 'TTL 15min · filesystem descartado'}
            on={true}
          />
          <SettingRow
            label="Retenção de auditoria"
            value={security.audit_retention_days
              ? `Snapshots por ${security.audit_retention_days} dias`
              : 'Snapshots por 90 dias'}
            on={true}
            last
          />
        </Card>

        {/* Membros & papéis */}
        <Card>
          <div className="flex items-center gap-2 mb-2">
            <Users size={16} style={{ color: C.cyber }} aria-hidden="true" />
            <h3 style={{ fontSize: 15, fontWeight: 600, color: C.neutral }}>
              Membros &amp; papéis
            </h3>
          </div>
          {members.length > 0 ? (
            members.map((mem, i) => (
              <SettingRow
                key={mem.name ?? mem.email}
                label={mem.name ?? mem.email}
                value={`${mem.role} · ${mem.matrix_name}`}
                last={i === members.length - 1}
              />
            ))
          ) : (
            /* Fallback visual */
            <>
              <SettingRow label="Carlos Oliveira" value="Tech Lead · Admin" />
              <SettingRow label="Maria Souza"     value="Product Owner · Manager" />
              <SettingRow label="João Mendes"     value="Product Owner · Manager" />
              <SettingRow label="Ana Lima"        value="Dev · Analyze" last />
            </>
          )}
        </Card>

        {/* Card de acesso à Matriz de Acessos — ocupa toda a largura */}
        <Card
          style={{ gridColumn: '1 / -1', borderColor: `${C.cyber}33` }}
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <ShieldCheck size={16} style={{ color: C.cyber }} aria-hidden="true" />
              <h3 style={{ fontSize: 15, fontWeight: 600, color: C.neutral }}>
                Matriz de Acessos
              </h3>
            </div>
            <Btn
              variant="outline"
              size="sm"
              icon={ChevronRight}
              onClick={() => navigate('accessMatrix')}
              aria-label="Ir para Gerenciamento de Matrizes e Usuários"
            >
              Gerenciar matrizes e usuários
            </Btn>
          </div>
          <p
            style={{
              fontSize: 13,
              color: C.neutralDim,
              lineHeight: 1.5,
              marginTop: 8,
              marginBottom: 0,
            }}
          >
            Defina perfis de permissão (matrizes) por recurso e ação e associe cada usuário a uma
            matriz. RBAC granular: Viewer &lt; Estimative &lt; Analyze &lt; Manager &lt; Admin.
          </p>
        </Card>
      </div>
    </div>
  );
}
